import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import { Camera, CheckCircle2, FolderPlus, Image, Plus, Sparkles } from 'lucide-react'
import { NewAlbum, NewFolder } from '../../../../Data/album.dto'
import { useAppDispatch, useAppSelector } from '../../../../Redux/Hooks'
import { createAlbumAPI } from '../../../../Redux/ApiCalls/Dashboard/AlbumAPI'
import imageCompression from 'browser-image-compression'
import { useNavigate } from 'react-router-dom'
import { clearError, clearFlagAlbums } from '../../../../Redux/Slice/Dashboard/AlbumSlice'
import LoadingDots from '../../../atoms/Utlis/LoadinDots'
import AddFolderModal from '../../../atoms/Dashboard/SingleAlbumPage/CreateFolderModal'
import FolderCard from '../../../atoms/Dashboard/SingleAlbumPage/FolderCard'
import { showErrorToast, showSuccessToast } from '../../../atoms/Utlis/Toast'
import BrandDatePicker from '../../../atoms/Utlis/BrandDatePicker'
import MarketplaceEmptyState from '../../../organisms/Dashboard/Marketplace/MarketplaceEmptyState'
import PermissionGate from '../../../atoms/Utlis/PermissionGate'
import {
  FormStack,
  FormCard,
  CardHeader,
  CardIcon,
  CardTitle,
  CardSubtitle,
  DetailsGrid,
  FieldsRow,
  Field,
  Label,
  Input,
  ErrorSpan,
  RequiredStar,
  CoverUploadArea,
  CoverDropzone,
  CoverHint,
  CoverSubhint,
  CoverPreviewWrap,
  CoverPreview,
  RemoveCoverBtn,
  FolderSectionHeader,
  FolderCount,
  AddFolderBtn,
  FolderGrid,
  SubmitBar,
  SubmitHint,
  SubmitButton,
} from './albumFormSharedStyles'

interface ValidationErrors {
  coverPhoto: string
  name: string
  date: string
  customerName: string
  customerMobile: string
}

const CreateNewAlbumPage: React.FC = () => {
  const [album, setAlbum] = useState<NewAlbum>({
    name: '',
    date: '',
    media_type: 1,
    image: '',
  })
  const [customerName, setCustomerName] = useState<string>('')
  const [customerMobile, setCustomerMobile] = useState<string>('')
  const [imagePreview, setImagePreview] = useState<string>('')
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [isCompressing, setIsCompressing] = useState<boolean>(false)
  const [activeButton, setActiveButton] = useState(true)
  const [totalImages, setTotalImages] = useState(0)
  const [folders, setFolders] = useState<NewFolder[] | []>([])
  const [errors, setErrors] = useState<ValidationErrors>({
    coverPhoto: '',
    name: '',
    date: '',
    customerName: '',
    customerMobile: '',
  })
  const [showErrors, setShowErrors] = useState(false)
  const [createFolderModal, setCreateFolderModal] = useState(false)
  const dispatch = useAppDispatch()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { isError, loading } = useAppSelector((state) => state.album)
  const [currentFolder, setCurrentFolder] = useState<NewFolder | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    return () => {
      dispatch(clearError())
      dispatch(clearFlagAlbums())
    }
  }, [dispatch, isError])

  useEffect(() => {
    isValidAlbum(album)
  }, [selectedImage, album, customerName, customerMobile])

  const handleImageChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      let file = event.target.files[0]
      setIsCompressing(true)
      const compressedBlob = await compressImage(file)
      file = blobToFile(compressedBlob, file.name)
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onloadend = () => {
        setSelectedImage(file)
        setIsCompressing(false)
        setImagePreview(reader.result as string)
      }
      isValidAlbum(album)
    }
  }

  const blobToFile = (blob: Blob, fileName: string): File =>
    new File([blob], fileName, { type: blob.type })

  const compressImage = async (file: File): Promise<Blob> => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: false,
    }
    try {
      return await imageCompression(file, options)
    } catch (error) {
      console.error('Error compressing image:', error)
      throw error
    }
  }

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    if (name === 'customerName') {
      setCustomerName(value)
    } else if (name === 'customerMobile') {
      const digitsOnly = value.replace(/\D/g, '')
      if (digitsOnly.length <= 10) setCustomerMobile(digitsOnly)
    } else {
      setAlbum((prev) => {
        if (name === 'date' || (name === 'name' && value.length < 25)) {
          return { ...prev, [name]: value }
        }
        return prev
      })
    }
  }

  const isValidPhoneNumber = (phone: string): boolean =>
    /^[6-9]\d{9}$/.test(phone)

  const isValidCustomerName = (name: string): boolean =>
    /^[a-zA-Z\s.'-]{2,50}$/.test(name.trim())

  const isValidDate = (dateString: string): boolean =>
    /^(\d{4})-(\d{2})-(\d{2})$/.test(dateString)

  const isValidAlbum = (albumData: NewAlbum) => {
    const newErrors: ValidationErrors = {
      coverPhoto: '',
      name: '',
      date: '',
      customerName: '',
      customerMobile: '',
    }

    if (!selectedImage) newErrors.coverPhoto = 'Please select a cover photo'
    if (albumData.name.length === 0) newErrors.name = 'Please enter an album name'
    if (!isValidDate(albumData.date)) newErrors.date = 'Please select a creation date'
    if (customerName.trim().length > 0) {
      if (!isValidCustomerName(customerName)) {
        newErrors.customerName = 'Please enter a valid client name (2-50 characters, letters only)'
      }
      if (customerMobile.length === 0) {
        newErrors.customerMobile = 'Phone number is compulsory when client name is entered'
      } else if (customerMobile.length < 10) {
        newErrors.customerMobile = 'Phone number must be 10 digits'
      } else if (!isValidPhoneNumber(customerMobile)) {
        newErrors.customerMobile = 'Please enter a valid 10-digit phone number starting with 6-9'
      }
    } else if (customerMobile.length > 0) {
      if (customerMobile.length < 10) {
        newErrors.customerMobile = 'Phone number must be 10 digits'
      } else if (!isValidPhoneNumber(customerMobile)) {
        newErrors.customerMobile = 'Please enter a valid 10-digit phone number starting with 6-9'
      }
    }

    setErrors(newErrors)

    const isClientNameValid =
      customerName.trim().length === 0 || isValidCustomerName(customerName)
    const isClientMobileValid =
      customerName.trim().length > 0
        ? customerMobile.length === 10 && isValidPhoneNumber(customerMobile)
        : customerMobile.length === 0 ||
          (customerMobile.length === 10 && isValidPhoneNumber(customerMobile))

    setActiveButton(
      !(
        isValidDate(albumData.date) &&
        albumData.name.length > 0 &&
        selectedImage &&
        isClientNameValid &&
        isClientMobileValid
      )
    )
  }

  const handleDivClick = () => {
    if (fileInputRef.current) fileInputRef.current.value = ''
    fileInputRef.current?.click()
  }

  const handleSubmit = async () => {
    isValidAlbum(album)
    setShowErrors(true)

    const isClientNameValid =
      customerName.trim().length === 0 || isValidCustomerName(customerName)
    const isClientMobileValid =
      customerName.trim().length > 0
        ? customerMobile.length === 10 && isValidPhoneNumber(customerMobile)
        : customerMobile.length === 0 ||
          (customerMobile.length === 10 && isValidPhoneNumber(customerMobile))

    if (
      !selectedImage ||
      album.name.length === 0 ||
      !isValidDate(album.date) ||
      !isClientNameValid ||
      !isClientMobileValid
    ) {
      return
    }

    if (selectedImage) {
      setAlbum((prev) => ({ ...prev, folders }))
      const formData = new FormData()
      formData.append('project[name]', album.name)
      formData.append('project[date]', album.date)
      formData.append('project[media_type]', '1')
      if (customerName.trim().length > 0) {
        formData.append('project[customer_name]', customerName.trim())
      }
      if (customerMobile.length > 0) {
        formData.append('project[customer_mobile]', customerMobile)
      }
      formData.append('project_image', selectedImage)

      folders.forEach((folder, folderIndex) => {
        formData.append(`folders[${folderIndex}][name]`, folder.name)
        folder.images.forEach((image, imageIndex) => {
          formData.append(
            `folders[${folderIndex}][images][${imageIndex}][image]`,
            image.image
          )
          formData.append(
            `folders[${folderIndex}][images][${imageIndex}][media_type]`,
            image.media_type.toString()
          )
        })
      })

      try {
        await dispatch(createAlbumAPI(formData)).unwrap()
        navigate(-1)
      } catch {
        // handled by reducer
      }
    }
  }

  const handleAddFolder = (folder: NewFolder) => {
    setFolders((prevFolders) => {
      const folderNameIndex = prevFolders.findIndex(
        (f) => f.name.trim().toLowerCase() === folder.name.trim().toLowerCase()
      )
      if (folderNameIndex !== -1) {
        showErrorToast('You can not add folder with same name.')
        return prevFolders
      }
      const folderIndex = prevFolders.findIndex((f) => f.id === folder.id)
      if (folderIndex !== -1) {
        const oldImageLength = prevFolders[folderIndex].images.length
        const newLength = folder.images.length
        setTotalImages((pre) => pre - oldImageLength + newLength)
        const updatedFolders = [...prevFolders]
        updatedFolders[folderIndex] = folder
        showSuccessToast('Folder added successfully.')
        return updatedFolders
      }
      if (prevFolders.length === 5) {
        showErrorToast('You can create 5 folders at once.')
        return prevFolders
      }
      setTotalImages((pre) => pre + folder.images.length)
      showSuccessToast('Folder added successfully.')
      return [...prevFolders, folder]
    })
  }

  const removeImageandPreview = () => {
    URL.revokeObjectURL(imagePreview)
    setImagePreview('')
    setSelectedImage(null)
  }

  const canSubmit = !activeButton
  const submitHint = canSubmit
    ? 'All set — create your album and start uploading photos.'
    : 'Add a cover photo, name, and date to continue.'

  return (
    <FormStack>
      <FormCard>
        <CardHeader>
          <CardIcon>
            <Image size={18} />
          </CardIcon>
          <div>
            <CardTitle>Album details</CardTitle>
            <CardSubtitle>Cover photo and basic information</CardSubtitle>
          </div>
        </CardHeader>

        <DetailsGrid>
          <CoverUploadArea onClick={handleDivClick}>
            <Label>Cover photo<RequiredStar>*</RequiredStar></Label>
            {imagePreview ? (
              <CoverPreviewWrap onClick={(e) => e.stopPropagation()}>
                <CoverPreview src={imagePreview} alt="Cover preview" onClick={handleDivClick} />
                <RemoveCoverBtn
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    removeImageandPreview()
                  }}
                  aria-label="Remove cover photo"
                >
                  ×
                </RemoveCoverBtn>
              </CoverPreviewWrap>
            ) : (
              <CoverDropzone>
                <Camera size={28} color="#c94daa" />
                <CoverHint>
                  {isCompressing ? 'Uploading…' : 'Add cover photo'}
                </CoverHint>
                <CoverSubhint>JPG or PNG · Best: 1920px wide</CoverSubhint>
              </CoverDropzone>
            )}
            {showErrors && errors.coverPhoto && (
              <ErrorSpan>{errors.coverPhoto}</ErrorSpan>
            )}
            <input
              type="file"
              accept=".jpg, .jpeg, .png"
              onChange={handleImageChange}
              style={{ display: 'none' }}
              ref={fileInputRef}
            />
          </CoverUploadArea>

          <div>
            <FieldsRow>
              <Field>
                <Label>Name<RequiredStar>*</RequiredStar></Label>
                <Input
                  type="text"
                  name="name"
                  value={album.name}
                  placeholder="Enter album name"
                  onChange={onChange}
                />
                {showErrors && errors.name && <ErrorSpan>{errors.name}</ErrorSpan>}
              </Field>
              <Field>
                <Label>Creation date<RequiredStar>*</RequiredStar></Label>
                <BrandDatePicker
                  name="date"
                  value={album.date}
                  required
                  onChange={(v) => setAlbum((prev) => ({ ...prev, date: v }))}
                />
                {showErrors && errors.date && <ErrorSpan>{errors.date}</ErrorSpan>}
              </Field>
            </FieldsRow>

            <FieldsRow>
              <Field>
                <Label>Client name</Label>
                <Input
                  type="text"
                  name="customerName"
                  value={customerName}
                  placeholder="Enter client name"
                  onChange={onChange}
                />
                {showErrors && errors.customerName && (
                  <ErrorSpan>{errors.customerName}</ErrorSpan>
                )}
              </Field>
              <Field>
                <Label>
                  Client phone
                  {customerName.trim().length > 0 && <RequiredStar>*</RequiredStar>}
                </Label>
                <Input
                  type="tel"
                  name="customerMobile"
                  value={customerMobile}
                  placeholder="10-digit phone number"
                  onChange={onChange}
                  maxLength={10}
                />
                {showErrors && errors.customerMobile && (
                  <ErrorSpan>{errors.customerMobile}</ErrorSpan>
                )}
              </Field>
            </FieldsRow>
          </div>
        </DetailsGrid>
      </FormCard>

      <FormCard>
        <FolderSectionHeader>
          <CardHeader style={{ margin: 0, flex: 1 }}>
            <CardIcon>
              <FolderPlus size={18} />
            </CardIcon>
            <div>
              <CardTitle>Folders</CardTitle>
              <CardSubtitle>
                Optional — add up to 5 folders while creating
                {folders.length > 0 && (
                  <FolderCount> · {folders.length}/5</FolderCount>
                )}
              </CardSubtitle>
            </div>
          </CardHeader>
          <AddFolderBtn
            type="button"
            onClick={() =>
              folders.length < 5
                ? setCreateFolderModal(true)
                : showErrorToast('You can create 5 folders while creating album.')
            }
          >
            <FolderPlus size={15} />
            Add folder
          </AddFolderBtn>
        </FolderSectionHeader>

        <AddFolderModal
          totalFoldersLength={folders.length}
          isOpen={createFolderModal}
          onRequestClose={() => {
            setCreateFolderModal(false)
            setCurrentFolder(null)
          }}
          imageLimit={20 - totalImages}
          onSubmit={handleAddFolder}
          currentFolder={currentFolder}
          setCurrentFolder={setCurrentFolder}
        />

        {folders.length > 0 ? (
          <FolderGrid>
            {folders.map((folder) => (
              <FolderCard
                key={folder.id}
                newFolder={folder}
                isNew={true}
                onClick={() => {
                  setCurrentFolder(folder)
                  setCreateFolderModal(true)
                }}
              />
            ))}
          </FolderGrid>
        ) : (
          <MarketplaceEmptyState
            padding="36px 20px"
            icon={<FolderPlus size={34} strokeWidth={1.5} />}
            title="No folders yet"
            description="Group photos into folders like Engagement, Reception, or Haldi — optional while creating your album."
            actions={[
              {
                label: 'Add folder',
                onClick: () =>
                  folders.length < 5
                    ? setCreateFolderModal(true)
                    : showErrorToast('You can create 5 folders while creating album.'),
                icon: <Plus size={15} />,
              },
            ]}
            hint={{
              icon: <Sparkles size={13} />,
              text: 'Add up to 5 folders · Upload photos after album is created',
            }}
          />
        )}
      </FormCard>

      <SubmitBar>
        <SubmitHint>{submitHint}</SubmitHint>
        {loading ? (
          <LoadingDots />
        ) : (
          <PermissionGate
            permission="albums.create"
            fallback={
              <div className="text-sm text-red-500 font-medium">
                You do not have permission to create albums (requires albums.create)
              </div>
            }
          >
            <SubmitButton type="button" $ready={canSubmit} onClick={handleSubmit}>
              {canSubmit && <CheckCircle2 size={18} />}
              Create album
            </SubmitButton>
          </PermissionGate>
        )}
      </SubmitBar>
    </FormStack>
  )
}

export default CreateNewAlbumPage
